import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, Search, LogIn } from 'lucide-react';
import { Dropdown, Space, Avatar, MenuProps } from 'antd';
import { UserOutlined, LogoutOutlined } from '@ant-design/icons';
import { AnimatePresence, motion } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { Logo } from '@/components/ui/Logo';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/hooks/useAuth';
import { useLogout } from '@/features/auth/hooks/useAuth';
import { trackSearchPerformed } from '@/lib/analytics/events';

interface MarketHeaderProps {
  /** Show the inline search box (hidden on narrow checkout-style pages if desired). */
  showSearch?: boolean;
}

/**
 * Shared marketplace top bar used across the public shopping experience
 * (landing, products, product detail, cart, checkout) so the chrome is
 * consistent and every page is navigable. Mostly-white, keeps brand colors.
 */
export const MarketHeader: React.FC<MarketHeaderProps> = ({ showSearch = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleCart, itemCount } = useCart();
  const { user } = useAuth();
  const { mutate: logout } = useLogout();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [scrolled, setScrolled] = useState(false);

  const isMarketplace = location.pathname === '/products';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    if (term.trim()) {
      trackSearchPerformed(term, mobileMenuOpen ? 'market_header_mobile' : 'market_header');
    }
    navigate(term.trim() ? `/products?search=${encodeURIComponent(term.trim())}` : '/products');
  };

  const navLinks = [
    { label: 'Home', path: '/', active: location.pathname === '/' },
    { label: 'Marketplace', path: '/products', active: isMarketplace },
    { label: 'Contact', path: '/contact', active: location.pathname === '/contact' },
  ];

  const userMenuItems: MenuProps['items'] = [
    { key: 'dashboard', icon: <UserOutlined />, label: 'My dashboard', onClick: () => navigate(`/${user?.role}`) },
    { key: 'orders', icon: <ShoppingCart size={14} />, label: 'My orders', onClick: () => navigate('/orders') },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Log out', danger: true, onClick: () => logout() },
  ];

  return (
    <header
      className={`sticky top-0 z-50 bg-white/85 dark:bg-slate-900/85 backdrop-blur-xl border-b transition-all duration-300 ${
        scrolled
          ? 'border-secondary-200/80 dark:border-secondary-800 shadow-[0_4px_24px_-8px_rgba(15,23,42,0.12)]'
          : 'border-secondary-100 dark:border-secondary-800/60'
      }`}
    >
      <div className="container mx-auto px-4 h-16 lg:h-[72px] flex items-center gap-4">
        {/* Brand */}
        <button className="flex items-center gap-2.5 flex-shrink-0 group" onClick={() => navigate('/')}>
          <Logo size="md" showWordmark={false} className="sm:hidden group-hover:scale-105 transition-transform duration-300" />
          <Logo size="md" showWordmark className="hidden sm:flex group-hover:scale-105 transition-transform duration-300" />
        </button>

        {/* Search */}
        {showSearch && (
          <form onSubmit={submitSearch} className="hidden md:flex items-center gap-2 flex-1 max-w-md">
            <div className="relative w-full group">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-secondary-400 group-focus-within:text-primary-500 transition-colors" size={18} />
              <input
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder="Search fresh produce, farms, tools…"
                className="w-full pl-10 pr-4 py-2.5 rounded-full bg-secondary-100/80 dark:bg-slate-800 border border-transparent focus:border-primary-500 focus:bg-white dark:focus:bg-slate-800 focus:ring-4 focus:ring-primary-500/10 text-sm text-secondary-900 dark:text-white placeholder:text-secondary-400 focus:outline-none transition-all"
              />
            </div>
          </form>
        )}

        {/* Desktop links */}
        <nav className="hidden lg:flex items-center gap-1 ml-auto">
          {navLinks.map((link) => (
            <button
              key={link.path}
              onClick={() => navigate(link.path)}
              className={`relative px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                link.active
                  ? 'text-primary-600 bg-primary-50 dark:bg-primary-900/20 dark:text-primary-400'
                  : 'text-secondary-600 dark:text-secondary-300 hover:text-primary-600 hover:bg-secondary-50 dark:hover:bg-slate-800'
              }`}
            >
              {link.label}
            </button>
          ))}
        </nav>

        {/* Actions */}
        <div className="flex items-center gap-1.5 lg:gap-3 ml-auto lg:ml-0">
          <button
            onClick={toggleCart}
            className="relative p-2.5 rounded-xl hover:bg-secondary-100 dark:hover:bg-slate-800 text-secondary-700 dark:text-secondary-200 transition-colors"
            aria-label="Open cart"
          >
            <ShoppingCart size={22} />
            {itemCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-primary-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center border-2 border-white dark:border-slate-900 shadow-sm">
                {itemCount}
              </span>
            )}
          </button>

          {user ? (
            <Dropdown menu={{ items: userMenuItems }} trigger={['click']} placement="bottomRight">
              <Space className="cursor-pointer hover:opacity-80 transition-opacity">
                <Avatar
                  icon={<UserOutlined />}
                  src={user.profileImage}
                  style={{ background: '#f97316' }}
                />
                <span className="hidden md:block text-sm font-semibold text-secondary-900 dark:text-white">
                  {user.firstName}
                </span>
              </Space>
            </Dropdown>
          ) : (
            <>
              <Button variant="ghost" onClick={() => navigate('/login')} className="hidden sm:flex gap-2">
                <LogIn size={18} /> Log in
              </Button>
              <Button onClick={() => navigate('/register')} className="rounded-full">
                Sign up
              </Button>
            </>
          )}

          <button
            className="lg:hidden p-2 rounded-xl text-secondary-600 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-slate-800 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Menu"
          >
            {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="lg:hidden overflow-hidden bg-white dark:bg-slate-900 border-t border-secondary-100 dark:border-secondary-800 shadow-lg"
          >
            <div className="p-4 space-y-4">
              {showSearch && (
                <form onSubmit={submitSearch} className="relative">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-secondary-400" size={18} />
                  <input
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                    placeholder="Search products…"
                    className="w-full pl-10 pr-4 py-2.5 rounded-full bg-secondary-100 dark:bg-slate-800 text-sm text-secondary-900 dark:text-white placeholder:text-secondary-400 focus:outline-none focus:ring-4 focus:ring-primary-500/10"
                  />
                </form>
              )}
              <div className="flex flex-col gap-1">
                {navLinks.map((link) => (
                  <button
                    key={link.path}
                    onClick={() => { navigate(link.path); setMobileMenuOpen(false); }}
                    className={`text-left px-3 py-2.5 rounded-xl font-semibold transition-colors ${
                      link.active
                        ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400'
                        : 'hover:bg-secondary-50 dark:hover:bg-slate-800 text-secondary-700 dark:text-secondary-200'
                    }`}
                  >
                    {link.label}
                  </button>
                ))}
              </div>
              {!user && (
                <div className="pt-3 border-t border-secondary-100 dark:border-secondary-800 flex flex-col gap-2">
                  <Button variant="outline" fullWidth onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}>Log in</Button>
                  <Button fullWidth onClick={() => { navigate('/register'); setMobileMenuOpen(false); }}>Sign up</Button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
